import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import { IWorkflowEdge, Workflow, WorkflowEdge, WorkflowVertex } from 'component.workflow.core';
import { AbstractWorkflowGraphDispatcherObserver } from './abstract-workflow-graph-dispatcher-observer';
import {
  WorkflowGraphDispatcherAction, WorkflowGraphDispatcherActionType as ActionType,
  WorkflowGraphDispatcherActionType,
  WorkflowGraphDispatcherService
} from './workflow-graph-dispatcher.service';
import { WorkflowGraphService } from './workflow-graph.service';
import { WorkflowGraphSelectionService } from './workflow-graph-selection.service';
import { HtmlDragSupport } from 'buzzyears.ui/index';
import { Subscription } from 'rxjs/Subscription';

@Component({
  selector: 'wf-graph',
  template: `

    <svg:svg class="svg-designer graph-bg workflow-designer"
             version="1.1"
             xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
             style="display: block; margin: auto;" [attr.width]="computedWidth"
             [style.background-size.px]="zoom * 100"
             (mousedown)="onMouseDown($event)"
             [attr.height]="computedHeight" [attr.viewBox]="viewBox">

      <style>

        text {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          cursor: default;
        }

        .graph-vertex {
          opacity: 0.85;
          transition: 500ms;
          transition-timing-function: ease-in-out;
          transition-property: opacity;
        }

        .graph-vertex:hover {
          opacity: 1;
        }

        .graph-vertex .io-type {
          opacity: 0;
          transition: 500ms;
          transition-timing-function: ease-in-out;
          transition-property: opacity;
          fill: #FC644B;
          visibility: hidden;
        }

        .graph-vertex:hover .io-type {
          opacity: 1;
          visibility: visible;
        }

        .graph-vertex .activated-io-type {
          opacity: 1;
          visibility: visible;
          fill: #50AE01;
        }

        .anchor-point {
          cursor: crosshair;
        }

        .highlighted-point {
          fill: #50AE01;
          stroke: #50AE01;
        }
      </style>

      <svg:defs>
        <marker id="ConnectionHead"
                viewBox="0 0 20 20"
                refX="50" refY="10"
                markerWidth="10"
                markerHeight="10"
                fill="#555"
                orient="auto">
          <path d="M 0 0 L 20 10 L 0 20 z"/>
        </marker>
        <marker id="NewConnectionHead"
                viewBox="0 0 20 20"
                refX="50" refY="10"
                markerWidth="10"
                markerHeight="10"
                fill="#50AE01"
                orient="auto">
          <path d="M 0 0 L 20 10 L 0 20 z"/>
        </marker>
        <filter x="0" y="0" width="1" height="1" id="edgeValue">
          <feFlood flood-color="black"/>
          <feComposite in="SourceGraphic"/>
        </filter>
      </svg:defs>

      <svg:g wf-graph-vertex *ngFor="let vertex of vertexList" [vertex]="vertex" [zoom]="zoom"
             [editable]="editable"/>

      <svg:g wf-graph-edge *ngFor="let edge of edgeList" [edge]="edge"/>

      <svg:g *ngIf="editable" wf-graph-draw-edge [zoom]="zoom"/>

      <svg:rect *ngIf="selectionPlaceholder && selectionPlaceholder.active"
                [attr.x]="selectionPlaceholder.x"
                [attr.y]="selectionPlaceholder.y"
                [attr.width]="selectionPlaceholder.width"
                [attr.height]="selectionPlaceholder.height"
                stroke-dasharray="5,5"
                stroke-width="2"
                stroke="#FF00FF"
                fill="#FF00FF22"/>

    </svg:svg>

  `,
  styles: [`

    :host {
      height: 97%;
      overflow: auto;
      background-color: #fff;
    }

    svg {

    }

  `],
  providers: [
    WorkflowGraphService,
    WorkflowGraphDispatcherService,
    WorkflowGraphSelectionService
  ]
})
export class WorkflowGraphComponent extends AbstractWorkflowGraphDispatcherObserver implements OnInit, OnDestroy {

  @Input() workflow: Workflow;

  @Input() zoom = 1;
  @Input() width = 12000;
  @Input() height = 7000;

  @Input() editable = true;

  dragMove$: Subscription;
  dragFinish$: Subscription;

  selectionPlaceholder: any = null;
  selectionStartingPoint: any = null;
  boundingBox: any = null;

  @Output() vertexSelected = new EventEmitter<WorkflowVertex>();
  @Output() multipleVerticesSelected = new EventEmitter<WorkflowVertex[]>();
  @Output() edgeAdded = new EventEmitter<IWorkflowEdge>();
  @Output() vertexMoved = new EventEmitter<WorkflowVertex>();

  protected selectionDragSupport: HtmlDragSupport;

  constructor(dispatcherService: WorkflowGraphDispatcherService,
              protected renderer: Renderer2,
              protected cdr: ChangeDetectorRef,
              protected graphSelectionService: WorkflowGraphSelectionService) {
    super(dispatcherService);

    this.selectionDragSupport = new HtmlDragSupport(renderer);

    this.dragMove$ = this.selectionDragSupport.dragMove.subscribe(location => this.setSelectionLocation(location));
    this.dragFinish$ = this.selectionDragSupport.dragFinish.subscribe(() => this.onSelectionDragFinish());
  }

  setSelection(vertices: WorkflowVertex[]) {
    this.graphSelectionService.updateSelection(vertices);
  }

  clearSelection() {
    this.graphSelectionService.updateSelection([]);
  }

  vertexRemoved(vertex: WorkflowVertex) {
    this.graphSelectionService.removeFromSelection(vertex);
  }

  onMouseDown(event: MouseEvent) {

    if (event.which !== 1) {
      // If its not a left click, ignore it.
      return;
    }

    if (event.shiftKey && this.editable) {
      // Its a drag action
      const zoom = this.zoom,
        bbox = (<any>event.target).getBoundingClientRect();

      this.boundingBox = bbox;

      const origin = {x: event.clientX, y: event.clientY};
      this.selectionDragSupport.activate(event, {
        x: (origin.x - bbox.x) / zoom - 10,
        y: (origin.y - bbox.y) / zoom - 10
      }, this.zoom);
      // console.log(zoom, bbox, origin, this.boundingBox);
      this.selectionStartingPoint = {x: (origin.x - bbox.x) / zoom, y: (origin.y - bbox.y) / zoom};
      this.selectionPlaceholder = {...this.selectionStartingPoint, width: 10, height: 10};

      event.stopPropagation();
      return false;
    }

  }

  setSelectionLocation(location: any) {
    const bbox = this.boundingBox,
      zoom = this.zoom;

    let x = (location.x),
      y = (location.y),
      width = this.selectionStartingPoint.x - x,
      height = this.selectionStartingPoint.y - y;

    if (width < 0) {
      x = this.selectionStartingPoint.x;
      width = width * -1;
    }

    if (height < 0) {
      y = this.selectionStartingPoint.y;
      height = height * -1;
    }

    // console.log(x, y, width, height, location);

    this.selectionPlaceholder = {
      x,
      y,
      width,
      height,
      active: true
    };

    for (const vertex of this.vertexList) {
      if (vertex.x > x && vertex.y > y && vertex.x < (x + width) && vertex.y < (y + height)) {
        this.graphSelectionService.addToSelection(vertex);
      } else {
        if (this.graphSelectionService.isSelected(vertex)) {
          this.graphSelectionService.removeFromSelection(vertex);
        }
      }
    }

    // this.dispatcherService.dispatchAction(ActionType.MultiSelectVertex, this, this.graphSelectionService.verticesSelected);
    // console.log('Selection:', location, this.selectionPlaceholder);
  }

  onSelectionDragFinish() {
    this.selectionPlaceholder = null;
    this.selectionStartingPoint = null;
    this.multipleVerticesSelected.emit(this.graphSelectionService.verticesSelected);
  }

  get viewBox(): string {
    return '0 0 ' + this.width + ' ' + this.height;
  }

  get computedWidth(): number {
    return this.width * this.zoom;
  }

  get computedHeight(): number {
    return this.height * this.zoom;
  }

  ngOnInit() {
    super.ngOnInit();
  }

  test(data: any) {
    let output;

    try {
      output = this.workflow.execute(data);
    } catch (e) {

    }

    this.dispatcherService.dispatchAction(WorkflowGraphDispatcherActionType.WorkflowTested, this, {
      input: data,
      output: output
    });

    return output;
  }

  manageMultiSelectedVertices(vertexList: WorkflowVertex[]): WorkflowVertex[] {
    return this.graphSelectionService.updateSelectionState(vertexList);
  }

  get vertexList(): WorkflowVertex[] {
    return this.workflow.vertices.values as WorkflowVertex[];
  }

  get edgeList(): WorkflowEdge[] {
    return this.workflow.edges.values as WorkflowEdge[];
  }

  vertexUpdated(vertex: WorkflowVertex) {
    this.dispatcherService.dispatchAction(WorkflowGraphDispatcherActionType.VertexUpdated, this, vertex);
  }

  dispatchVertexSelected(vertex: WorkflowVertex) {
    this.dispatcherService.dispatchAction(WorkflowGraphDispatcherActionType.VertexSelected, this, vertex);
  }

  onDispatcherEvent(action: WorkflowGraphDispatcherAction) {
    switch (action.type) {
      case WorkflowGraphDispatcherActionType.SelectVertex:
        this.graphSelectionService.updateSelection([]); // Clear selection
        this.multipleVerticesSelected.emit([]);
        this.vertexSelected.emit(action.data);
        this.dispatchVertexSelected(action.data);
        break;

      case WorkflowGraphDispatcherActionType.MultiSelectVertex:
        const items = this.manageMultiSelectedVertices(action.data);
        this.multipleVerticesSelected.emit(items);
        break;

      case WorkflowGraphDispatcherActionType.ConnectionCreateRequest:
        this.edgeAdded.emit(action.data);
        break;

      case WorkflowGraphDispatcherActionType.VertexMoved:
        this.vertexMoved.emit(action.data);
        break;
    }
  }

  selectVertex(vertex: WorkflowVertex) {
    this.dispatcherService.dispatchAction(WorkflowGraphDispatcherActionType.SelectVertex, this, vertex);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();

    this.selectionDragSupport.deactivate();

    if (this.dragFinish$) {
      this.dragFinish$.unsubscribe();
      this.dragFinish$ = null;
    }

    if (this.dragMove$) {
      this.dragMove$.unsubscribe();
      this.dragMove$ = null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/library/graph/workflow-graph.component.ts